import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {PosSizeService} from './pos-size.service';

export interface PosMenuTextSizePreset {
  /** Backend POS_MENU_TEXT_SIZE_CHOICES and the APK's PosMenuTextSize ids. */
  id: 'compact' | 'default' | 'large' | 'xlarge';
  label: string;
  /** Label size of every uniform-grid cell, in CSS px (before --pos-scale). */
  px: number;
}

/**
 * Merchant-adjustable "Menu Button Text Size" (parity with the business APK PR #7).
 *
 * The four presets set the --pos-menu-text-size CSS custom property on <html>.
 * Every uniform-grid cell label (category chip, sub-category chip, menu item,
 * modifier tile, sub-modifier tile) reads `var(--pos-menu-text-size, 15px)`, so a
 * change shows on the next paint without per-component work.
 *
 * Store-wide like the display size: the source of truth is the store's
 * store_data.pos_menu_text_size (backend PR order_api_backend#367; the PATCH takes the
 * key at the top level, the response and GET carry it inside store_data). localStorage
 * only caches the last-known value so the size applies on boot, before the store API
 * responds.
 *
 * ENG-5842: superseded by the Text size slider (PosSizeService). The preset is now only
 * its legacy input, for stores that never moved the slider.
 */
@Injectable({providedIn: 'root'})
export class PosMenuTextSizeService {

  static readonly STORAGE_KEY = 'posMenuTextSize';
  static readonly CSS_VAR = '--pos-menu-text-size';

  readonly presets: PosMenuTextSizePreset[] = [
    {id: 'compact', label: 'Compact', px: 13},
    {id: 'default', label: 'Default', px: 15},
    {id: 'large', label: 'Large', px: 17},
    {id: 'xlarge', label: 'Extra Large', px: 19},
  ];

  private currentSubject = new BehaviorSubject<PosMenuTextSizePreset>(this.presets[1]);

  constructor(private size: PosSizeService) {}

  get current(): PosMenuTextSizePreset {
    return this.currentSubject.value;
  }

  get current$(): Observable<PosMenuTextSizePreset> {
    return this.currentSubject.asObservable();
  }

  /** The first web build cached the 13px preset as 'small'; the shared id is 'compact'. */
  private static readonly LEGACY_IDS: { [id: string]: PosMenuTextSizePreset['id'] } = { small: 'compact' };

  private findPreset(id: string | null | undefined): PosMenuTextSizePreset | undefined {
    const mapped = (id && PosMenuTextSizeService.LEGACY_IDS[id]) || id;
    return this.presets.find(p => p.id === mapped);
  }

  /** Restore the cached preset and apply it. Called once at app start. */
  init(): void {
    const savedId = localStorage.getItem(PosMenuTextSizeService.STORAGE_KEY);
    const preset = this.findPreset(savedId);
    if (preset) {
      this.currentSubject.next(preset);
    }
    this.apply(this.current);
  }

  setPreset(id: PosMenuTextSizePreset['id']): void {
    const preset = this.presets.find(p => p.id === id);
    if (!preset || preset.id === this.current.id) {
      return;
    }
    localStorage.setItem(PosMenuTextSizeService.STORAGE_KEY, preset.id);
    this.currentSubject.next(preset);
    this.apply(preset);
  }

  /**
   * Apply the store-wide size coming from backend store data. An unset or unknown
   * value is a no-op so stores that never chose a size keep the local value.
   */
  syncFromStore(sizeId: string | null | undefined): void {
    if (!sizeId || sizeId === this.current.id) {
      return;
    }
    const preset = this.findPreset(sizeId);
    if (!preset || preset.id === this.current.id) {
      return;
    }
    localStorage.setItem(PosMenuTextSizeService.STORAGE_KEY, preset.id);
    this.currentSubject.next(preset);
    this.apply(preset);
  }

  private apply(preset: PosMenuTextSizePreset): void {
    this.size.setLegacy({menuPx: preset.px});
  }
}
